import { cva, type VariantProps } from "class-variance-authority";
import type { ButtonHTMLAttributes } from "react";
import { cn } from "@/lib/cn";

const button = cva(
  "inline-flex items-center justify-center gap-2 rounded-md border font-medium transition-colors disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        default: "border-transparent bg-primary text-primary-foreground hover:opacity-90",
        outline: "border-border bg-card text-foreground hover:bg-accent",
        ghost: "border-transparent bg-transparent text-muted-foreground hover:bg-accent hover:text-foreground",
        /* Destructive actions are outlined rather than filled: a solid red
           button is the easiest thing on a page to hit by accident. */
        danger: "border-danger bg-transparent text-danger hover:bg-danger/10",
      },
      size: {
        sm: "h-7 px-2 text-xs",
        md: "h-9 px-3 text-sm",
        icon: "h-8 w-8",
      },
    },
    defaultVariants: { variant: "default", size: "md" },
  },
);

export type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & VariantProps<typeof button>;

export function Button({ className, variant, size, type, ...props }: ButtonProps) {
  return (
    <button
      // Defaulted because a button inside a form without it submits, which is
      // how a "cancel" ends up saving.
      type={type ?? "button"}
      className={cn(button({ variant, size }), className)}
      {...props}
    />
  );
}
